/**
 * Easypay payout onboarding (promoter side).
 *
 * there2b runs on split payments: a brand's payment is divided at source and
 * the promoter's share goes straight to their own Easypay account. A promoter
 * with no account id on file has nowhere for that share to land, so since
 * T2B-423 event creation stays locked until they have one.
 *
 * The promoter never supplies this value. They register on Easypay's own site
 * via the link below, Easypay hands there2b the resulting promoter→CIN list,
 * and an admin injects it. The API rejects the field on signup and profile
 * update, because it decides where money lands and only a server-side probe can
 * tell a genuine CIN from a well-formed one. Everything here is read-only.
 *
 * What the promoter CAN tell us is where they are in Easypay's own flow — see
 * EASYPAY_CHOICE below. That answer is a work queue for there2b, not a key: it
 * unlocks nothing, here or in the API.
 */
/**
 * Easypay's two doors, which are NOT the same page (checked 2026-09-21):
 *
 *   /signup?partnership=there2b  the registration form, headed "easypay
 *                                membership via: THERE2B LDA". The partnership
 *                                parameter is what attributes the new account
 *                                to there2b, so it must survive any edit here.
 *   (root)                       the log-in page.
 *
 * Sending a promoter who already signed up to the first one is the bug this
 * pair exists to prevent: they land back on a form they've completed, with no
 * way to find out where their application got to. What they want is their own
 * account — the only place that can show them KYC progress, since there2b
 * learns nothing until an account id arrives.
 *
 * Hardcoded defaults, env override. Both URLs are stable and neither was ever
 * set outside local development, so a default that just works beats config
 * that silently isn't there. Sandbox doesn't need a different host either —
 * easypay switches to test mode from inside the log-in page. The env vars stay
 * as an escape hatch for the day easypay moves something.
 */
const DEFAULT_ONBOARDING_URL = 'https://id.easypay.pt/signup?partnership=there2b';
const DEFAULT_LOGIN_URL = 'https://id.easypay.pt';

const envUrl = (value, fallback) => {
  const trimmed = String(value || '').trim();
  return trimmed || fallback;
};

export const EASYPAY_ONBOARDING_URL = envUrl(
  process.env.REACT_APP_EASYPAY_ONBOARDING_URL,
  DEFAULT_ONBOARDING_URL
);

export const EASYPAY_LOGIN_URL = envUrl(
  process.env.REACT_APP_EASYPAY_LOGIN_URL,
  DEFAULT_LOGIN_URL
);

/**
 * Easypay's public site — a third door, and the only one that isn't part of the
 * flow: it answers "who are these people I'm about to hand my bank details to".
 * Deliberately not id.easypay.pt, which is the account portal and says nothing
 * about the company. This is what the word "Easypay" links to in body copy.
 */
export const EASYPAY_SITE_URL = envUrl(
  process.env.REACT_APP_EASYPAY_SITE_URL,
  'https://www.easypay.pt/'
);

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Easypay account ids are UUIDs; anything else can never resolve as a split. */
const isValidEasypayAccountId = (value) =>
  UUID_RE.test(String(value || '').trim());

export const hasEasypayPayoutAccount = (promoter) =>
  isValidEasypayAccountId(promoter?.easypayAccountId);

/**
 * The promoter's own answer to "have you set up Easypay?", stored on their
 * profile so there2b knows whose CIN to go chasing. Mirrors
 * src/utils/easypayOnboarding.js in the API.
 */
export const EASYPAY_CHOICE = {
  /** "I created my account and filled in my business details." */
  DECLARED: 'declared',
  /** "I'd rather do this later — I understand my account stays on hold." */
  DEFERRED: 'deferred',
};

export const hasDeclaredEasypaySignup = (promoter) =>
  promoter?.easypayOnboardingChoice === EASYPAY_CHOICE.DECLARED;

/**
 * Payout status has exactly TWO values, and `easypayAccountId` decides both.
 *
 * There is no "awaiting validation" in between, deliberately. It would have to
 * be inferred from the promoter's own `easypayOnboardingChoice`, and there2b
 * has no way to check that claim — Easypay tells us nothing until an account
 * id turns up. A promoter who ticked the box but never finished (or was
 * rejected) would sit on a reassuring amber "we're validating you" forever,
 * which is worse than the honest answer. So: not connected until the id
 * exists, and the button sends them to Easypay to see their own real status.
 */
export const EASYPAY_STATUS = {
  PENDING: 'pending',
  CONNECTED: 'connected',
};

export const easypayPayoutStatus = (promoter) =>
  hasEasypayPayoutAccount(promoter)
    ? EASYPAY_STATUS.CONNECTED
    : EASYPAY_STATUS.PENDING;
